/**
 * The Review step's SolarAPP+ panel (US designs; docs/integrations/solarapp-plus.md): the verdict ("Eligible for
 * SolarAPP+" or exactly why not, each rule with its source), the AHJ's participation, "Download prefilled answers"
 * (PDF, JSON), the answers themselves, and, when the company connected SolarAPP+ and the server has the API on,
 * "Submit to SolarAPP+" with the status of each submission (refreshable). Submitting costs no Lux: the installer pays
 * SolarAPP+'s fee in SolarAPP+.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, ChevronRight, CircleHelp, ExternalLink, FileDown, FileJson, RefreshCw, Send, XCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { Badge, Button, Card, Modal, Spinner, cx, useToast } from "../components/ui";
import { shownVersion } from "../lib/format";
import { orderRules, statusTone, submitState, verdictTone, type RuleStatus, type SubmissionStatus, type Verdict } from "../lib/solarapp";
import { useIntlShape, useT, type Translate } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { DirIcon } from "../i18n/mirror";
import { useFormat } from "./format";
import type { Ws } from "./PermitWorkspace";
import "./solarapp.css";

function save(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function useSolarAppReview(designId: string, version: number | undefined, body: S["PlanSetInputs"], enabled = true) {
  return useQuery({
    queryKey: ["solarapp-review", designId, version, body],
    enabled: !!version && enabled,
    queryFn: () => unwrap(api.POST("/v1/designs/{design_id}/versions/{version_number}/solarapp", { params: { path: { design_id: designId, version_number: version! } }, body })),
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 30_000,
  });
}

const RULE_ICON: Record<RuleStatus, typeof CheckCircle2> = { pass: CheckCircle2, fail: XCircle, check: CircleHelp };

// lib/solarapp.ts's English labels, as catalog keys (same ids)
const RULE_LABEL: Record<RuleStatus, MessageKey> = { pass: "permit.solarApp.rule.pass", fail: "permit.solarApp.rule.fail", check: "permit.solarApp.rule.check" };

const STATUS_LABEL: Record<SubmissionStatus, MessageKey> = {
  created: "permit.solarApp.status.created",
  submitted: "permit.solarApp.status.submitted",
  approved: "permit.solarApp.status.approved",
  corrections: "permit.solarApp.status.corrections",
  cancelled: "permit.solarApp.status.cancelled",
  error: "permit.solarApp.status.error",
};

const STATUS_HINT: Record<SubmissionStatus, MessageKey> = {
  created: "permit.solarApp.statusHint.created",
  submitted: "permit.solarApp.statusHint.submitted",
  approved: "permit.solarApp.statusHint.approved",
  corrections: "permit.solarApp.statusHint.corrections",
  cancelled: "permit.solarApp.statusHint.cancelled",
  error: "permit.solarApp.statusHint.error",
};

/** submitState()'s reasons (English, lib/solarapp.ts) by their text. */
const SUBMIT_REASON: Record<string, MessageKey> = {
  "Submitting from Solar OS opens once SolarAPP+ grants integrator access. Download the prefilled answers and fill SolarAPP+ by hand.": "permit.solarApp.submit.noApi",
  "Connect your SolarAPP+ account first.": "permit.solarApp.submit.notConnected",
  "You can view this design; ask an admin to submit it.": "permit.solarApp.submit.viewOnly",
  "Fix the reasons above first: SolarAPP+ would refuse this design.": "permit.solarApp.submit.notEligible",
};

function verdictText(t: Translate, status: Verdict, toConfirm: number): string {
  if (status === "eligible") return t("permit.solarApp.verdict.eligible");
  if (status === "not_eligible") return t("permit.solarApp.verdict.notEligible");
  return t("permit.solarApp.verdict.confirm", { count: toConfirm });
}

function statusLabel(t: Translate, status: string): string {
  const key = STATUS_LABEL[status as SubmissionStatus];
  return key ? t(key) : status;
}

export function SolarAppPanel({ ws }: { ws: Ws }) {
  const country = ws.preview.data?.country;
  const review = useSolarAppReview(ws.designId, ws.version, ws.body, country === "US");
  const t = useT();
  const intl = useIntlShape();
  const [showPassed, setShowPassed] = useState(false);
  const [showAnswers, setShowAnswers] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  const download = useMutation({
    mutationFn: async (format: "pdf" | "json") => {
      const response = await rawFetch(`/api/v1/designs/${ws.designId}/versions/${ws.version}/solarapp/answers?format=${format}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ws.body),
      });
      const name = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? `solarapp-answers.${format}`;
      save(await response.blob(), name);
      return name;
    },
    onSuccess: (name) => toast(t("permit.solarApp.downloading", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
  const submit = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/versions/{version_number}/solarapp/submissions", { params: { path: { design_id: ws.designId, version_number: ws.version } }, body: ws.body })),
    onSuccess: () => {
      setConfirming(false);
      toast(t("permit.solarApp.created"));
      void qc.invalidateQueries({ queryKey: ["solarapp-review", ws.designId] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });

  if (country && country !== "US") return null;
  const data = review.data;
  if (!data) {
    return (
      <Card title="SolarAPP+" className="sa-panel">
        {review.error ? <p className="small muted">{errorMessage(review.error)}</p> : <Spinner label={t("permit.solarApp.checking")} />}
      </Card>
    );
  }
  const e = data.eligibility;
  const rules = orderRules(e.rules);
  const open = rules.filter((r) => r.status !== "pass");
  const passed = rules.filter((r) => r.status === "pass");
  const sub = submitState({ country: data.country, verdict: e.status, apiEnabled: data.connection.api_enabled, connected: data.connection.connected, canEdit: ws.canEdit });
  const ahj = data.ahj;

  return (
    <Card
      title="SolarAPP+"
      className={cx("sa-panel", review.isFetching && "pw-stale")}
      description={t("permit.solarApp.description")}
    >
      <div className={cx("sa-verdict", e.status)} role="status">
        {e.status === "eligible" ? <CheckCircle2 size={18} /> : e.status === "not_eligible" ? <XCircle size={18} /> : <CircleHelp size={18} />}
        <div>
          <b>{verdictText(t, e.status, e.to_confirm.length)}</b>
          <div className="small">
            {ahj.participates === true
              ? t("permit.solarApp.ahj.participates", {
                  name: ahj.state ? t("permit.solarApp.ahj.nameState", { name: ahj.name ?? "", state: ahj.state }) : (ahj.name ?? ""),
                  electrical: ahj.scope === "electrical" ? "yes" : "no",
                  suggested: ahj.matched_by === "suggested" ? "yes" : "no",
                })
              : ahj.participates === false
                ? ahj.name
                  ? t("permit.solarApp.ahj.notParticipating", { name: ahj.name })
                  : t("permit.solarApp.ahj.notParticipatingUnnamed")
                : ahj.name
                  ? t("permit.solarApp.ahj.unconfirmed", { name: ahj.name })
                  : t("permit.solarApp.ahj.none")}
          </div>
        </div>
        <Badge tone={verdictTone(e.status)}>{e.status === "eligible" ? t("permit.solarApp.badge.eligible") : e.status === "confirm" ? t("permit.solarApp.badge.toConfirm", { count: e.to_confirm.length }) : t("permit.solarApp.badge.reasons", { count: e.reasons.length })}</Badge>
      </div>

      {open.length > 0 && (
        <ul className="sa-rules">
          {open.map((r) => (
            <Rule key={r.id} r={r} />
          ))}
        </ul>
      )}
      <button className="link-btn sa-toggle" onClick={() => setShowPassed((v) => !v)} aria-expanded={showPassed}>
        {showPassed ? <ChevronDown size={14} /> : <DirIcon><ChevronRight size={14} /></DirIcon>} {t("permit.solarApp.passed", { count: passed.length })}
      </button>
      {showPassed && (
        <ul className="sa-rules passed">
          {passed.map((r) => (
            <Rule key={r.id} r={r} />
          ))}
        </ul>
      )}

      <div className="sa-actions">
        <Button icon={<FileDown size={16} />} loading={download.isPending && download.variables === "pdf"} onClick={() => download.mutate("pdf")}>
          {t("permit.solarApp.downloadAnswers")}
        </Button>
        <Button variant="ghost" size="sm" icon={<FileJson size={14} />} loading={download.isPending && download.variables === "json"} onClick={() => download.mutate("json")} title={t("permit.solarApp.jsonHint")}>
          {t("permit.solarApp.json")}
        </Button>
        {sub.show && (
          <Button variant="primary" icon={<Send size={16} />} disabled={sub.disabled} loading={submit.isPending} onClick={() => setConfirming(true)}>
            {t("permit.solarApp.submit.button")}
          </Button>
        )}
      </div>
      {sub.reason && (
        <p className="small muted sa-reason">
          {SUBMIT_REASON[sub.reason] ? t(SUBMIT_REASON[sub.reason]!) : sub.reason}{" "}
          {data.connection.api_enabled && !data.connection.connected && <Link to="/solarapp">{t("permit.solarApp.connect")}</Link>}
        </p>
      )}
      <p className="small muted sa-fee">{data.fee_note}</p>

      <button className="link-btn sa-toggle" onClick={() => setShowAnswers((v) => !v)} aria-expanded={showAnswers}>
        {showAnswers ? <ChevronDown size={14} /> : <DirIcon><ChevronRight size={14} /></DirIcon>} {t("permit.solarApp.answersToggle", { count: data.sections.reduce((n, s) => n + s.answers.length, 0) })}
      </button>
      {showAnswers && <Answers sections={data.sections} />}

      {data.submissions.length > 0 && <Submissions designId={ws.designId} items={data.submissions} offset={ws.doc.version_offset} />}

      {confirming && (
        <Modal
          title={t("permit.solarApp.submit.title")}
          onClose={() => setConfirming(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
              <Button variant="primary" icon={<Send size={16} />} loading={submit.isPending} onClick={() => submit.mutate()}>{t("permit.solarApp.submit.confirm")}</Button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {t("permit.solarApp.submit.body", {
              account: data.connection.account_name ?? data.connection.api_host ?? "",
              withPlanSet: data.plan_set_number ? "yes" : "no",
              number: data.plan_set_number ?? 0,
            })}
          </p>
          <p>{t("permit.solarApp.submit.fee")}</p>
          {e.status === "confirm" && <p className="small muted">{t("permit.solarApp.submit.confirmFirst", { items: intl.formatList(e.to_confirm.map((r) => r.title), { type: "conjunction" }) })}</p>}
        </Modal>
      )}
    </Card>
  );
}

function Rule({ r }: { r: S["SolarAppRule"] }) {
  const t = useT();
  const Icon = RULE_ICON[r.status];
  return (
    <li className={cx("sa-rule", r.status)}>
      <Icon size={15} className="sa-rule-ic" />
      <div>
        <div className="sa-rule-line">
          <b>{r.title}</b>
          <span className="sa-rule-status">{RULE_LABEL[r.status] ? t(RULE_LABEL[r.status]) : r.status}</span>
        </div>
        <div className="small">{r.detail}</div>
        <div className="small muted sa-source">
          {r.source_url ? (
            <a href={r.source_url} target="_blank" rel="noreferrer noopener">
              {r.source} <DirIcon><ExternalLink size={11} /></DirIcon>
            </a>
          ) : (
            r.source
          )}
        </div>
      </div>
    </li>
  );
}

function Answers({ sections }: { sections: S["SolarAppSection"][] }) {
  const t = useT();
  return (
    <div className="sa-answers">
      {sections.map((s) => (
        <table key={s.id} className="sa-answer-table">
          <caption>{s.title}</caption>
          <tbody>
            {s.answers.map((a) => (
              <tr key={a.key}>
                <th scope="row">{a.label}</th>
                <td className={a.value == null ? "muted" : undefined}>{a.value ?? t("permit.solarApp.notEntered")}</td>
                <td className="small muted">{a.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
}

function Submissions({ designId, items, offset }: { designId: string; items: S["SolarAppSubmission"][]; offset?: number | null }) {
  const t = useT();
  const f = useFormat();
  const toast = useToast();
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/designs/{design_id}/solarapp/submissions/{submission_id}/refresh", { params: { path: { design_id: designId, submission_id: id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["solarapp-review", designId] }),
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="sa-submissions">
      <div className="small muted sa-sub-head">{t("permit.solarApp.submissions")}</div>
      <ul>
        {items.map((s) => {
          const status = s.status as SubmissionStatus;
          return (
            <li key={s.id}>
              <div className="sa-sub-line">
                <Badge tone={statusTone(status)}>{statusLabel(t, status)}</Badge>
                <span className="small">
                  {s.permit_number
                    ? t("permit.solarApp.designVersionPermit", { version: shownVersion(s.version_number, offset), permit: s.permit_number })
                    : t("permit.solarApp.designVersion", { version: shownVersion(s.version_number, offset) })}
                </span>
                <span className="small muted">{f.date(s.updated_at)}</span>
                <span className="sa-sub-actions">
                  {s.remote_url && (
                    <a className="btn btn-ghost btn-sm" href={s.remote_url} target="_blank" rel="noreferrer noopener">
                      {t("permit.solarApp.open")} <DirIcon><ExternalLink size={12} /></DirIcon>
                    </a>
                  )}
                  <button className="icon-btn" title={t("permit.solarApp.refresh")} aria-label={t("permit.solarApp.refresh")} disabled={refresh.isPending} onClick={() => refresh.mutate(s.id)}>
                    <RefreshCw size={14} className={refresh.isPending && refresh.variables === s.id ? "pw-spin" : undefined} />
                  </button>
                </span>
              </div>
              <div className="small muted">{s.detail ?? (STATUS_HINT[status] ? t(STATUS_HINT[status]) : null)}</div>
              {s.history.length > 1 && (
                <ol className="sa-history small muted">
                  {s.history.map((h, i) => (
                    <li key={i}>
                      {statusLabel(t, h.status)} · {f.date(h.at)}
                      {h.detail ? ` · ${h.detail}` : ""}
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
